import React, {
  createContext,
  PropsWithChildren,
  useState,
  useEffect,
  useContext,
  useCallback,
  useMemo,
} from 'react';
import { getStagedVoyages, getStagedVoyagesCount } from '../../staging';
import {
  ReportingFiltersContextValue,
  ReportingFiltersProvider,
  useReportingFilters,
} from './reporting/ReportingFiltersContext';
import {
  ReportingChartDataContextValue,
  ReportingChartDataProvider,
  useReportingChartData,
} from './reporting/ReportingChartDataContext';
import {
  ReportingVoyageDataContextValue,
  ReportingVoyageDataProvider,
  useReportingVoyageData,
} from './reporting/ReportingVoyageDataContext';
import {
  ReportingShipmentDataContextValue,
  ReportingShipmentDataProvider,
  useReportingShipmentData,
} from './reporting/ReportingShipmentDataContext';
import {
  ReportingStockDataContextValue,
  ReportingStockDataProvider,
  useReportingStockData,
} from './reporting/ReportingStockDataContext';
import {
  ReportingShipViewDataContextValue,
  ReportingShipViewDataProvider,
  useReportingShipViewData,
} from './reporting/ReportingShipViewDataContext';
import type { ApiRecord } from './reporting/types';

// ── Coordination context ──────────────────────────────────────────────
// Manages tab-scoped lazy fetching and staged-voyage state that span
// across all domain-specific sub-contexts.
const ReportingCoordinationContext =
  createContext<ReportingCoordinationContextValue | null>(null);

// Data "buckets" backing each tab of VesselSalesHomePage — each is only fetched
// when its tab becomes active, instead of eagerly fetching all of them upfront.
export const TAB_DATA_KEYS = {
  CHARTS: 'charts',
  VOYAGE: 'voyage',
  SHIP_VIEW: 'shipView',
  STOCK: 'stock',
} as const;

export type TabDataKey = (typeof TAB_DATA_KEYS)[keyof typeof TAB_DATA_KEYS];

const ALL_TAB_DATA_KEYS: TabDataKey[] = Object.values(TAB_DATA_KEYS);

export interface ReportingCoordinationContextValue {
  isLoading: boolean;
  staleTabs: Set<TabDataKey>;
  activeTabKey: TabDataKey | null;
  loadingTabKey: TabDataKey | null;
  tabFetchers: Record<TabDataKey, () => Promise<void>>;
  invalidateAllTabs: () => void;
  setActiveTab: (tabKey: TabDataKey | null | undefined) => void;
  fetchAllData: () => Promise<void>;
  stagedVoyages: ApiRecord[];
  stagedCount: number;
  fetchStagedVoyages: () => Promise<void>;
  fetchStagedCount: () => Promise<void>;
}

const ReportingCoordinationProvider = ({ children }: PropsWithChildren) => {
  const { filters, activeScenario } = useReportingFilters();
  const { fetchChartsTabData } = useReportingChartData();
  const { fetchVoyagesPage, fetchTbnVoyages, resetSelectedVoyageId } =
    useReportingVoyageData();
  const { fetchShipmentDetailsPage } = useReportingShipmentData();
  const { fetchStockData } = useReportingStockData();
  const { fetchShipViewPage } = useReportingShipViewData();

  const [isLoading, setIsLoading] = useState(false);
  const [stagedVoyages, setStagedVoyages] = useState<ApiRecord[]>([]);
  const [stagedCount, setStagedCount] = useState(0);

  // ── Tab-scoped fetch bundles ──
  // Each bundle covers exactly the data one VesselSalesTabPanel tab renders,
  // so switching tabs never triggers requests for data the user isn't looking at.
  const fetchVoyageTabData = useCallback(async () => {
    const today = new Date().toISOString().slice(0, 10); // YYYY-MM-DD
    await Promise.all([
      fetchShipmentDetailsPage({ nearDate: today }),
      fetchVoyagesPage(1),
      fetchTbnVoyages(),
    ]);
  }, [fetchShipmentDetailsPage, fetchVoyagesPage, fetchTbnVoyages]);

  const fetchShipViewTabData = useCallback(
    () => fetchShipViewPage(1),
    [fetchShipViewPage]
  );

  const fetchStockTabData = useCallback(
    () => fetchStockData(),
    [fetchStockData]
  );

  const tabFetchers = useMemo<ReportingCoordinationContextValue['tabFetchers']>(
    () => ({
      [TAB_DATA_KEYS.CHARTS]: fetchChartsTabData,
      [TAB_DATA_KEYS.VOYAGE]: fetchVoyageTabData,
      [TAB_DATA_KEYS.SHIP_VIEW]: fetchShipViewTabData,
      [TAB_DATA_KEYS.STOCK]: fetchStockTabData,
    }),
    [
      fetchChartsTabData,
      fetchVoyageTabData,
      fetchShipViewTabData,
      fetchStockTabData,
    ]
  );

  // Tabs whose bundle needs a (re)fetch before being shown again.
  const [staleTabs, setStaleTabs] = useState<Set<TabDataKey>>(
    () => new Set(ALL_TAB_DATA_KEYS)
  );
  const [activeTabKey, setActiveTabKey] = useState<TabDataKey | null>(null);
  // Which tab's bundle is currently being (re)fetched — drives the tab loading indicators.
  const [loadingTabKey, setLoadingTabKey] = useState<TabDataKey | null>(null);

  // Marks every tab bundle stale — used after filter/scenario changes and
  // after mutations, so each tab refetches next time it becomes active.
  const invalidateAllTabs = useCallback(() => {
    setStaleTabs(new Set(ALL_TAB_DATA_KEYS));
  }, []);

  // Kept for backward compatibility with existing callers expecting a full refresh:
  // invalidates every tab, the currently active one refetches immediately below.
  const fetchAllData = useCallback(async () => {
    invalidateAllTabs();
  }, [invalidateAllTabs]);

  const setActiveTab = useCallback((tabKey: TabDataKey | null | undefined) => {
    setActiveTabKey(tabKey ?? null);
  }, []);

  // Fetches the active tab's bundle whenever it becomes active while stale
  // (first visit, or after invalidateAllTabs/fetchAllData was called).
  useEffect(() => {
    if (!activeTabKey || !staleTabs.has(activeTabKey)) return;
    const fetcher = tabFetchers[activeTabKey];
    if (!fetcher) return;

    setStaleTabs((prev) => {
      const next = new Set(prev);
      next.delete(activeTabKey);
      return next;
    });

    setIsLoading(true);
    setLoadingTabKey(activeTabKey);
    fetcher().finally(() => {
      setIsLoading(false);
      setLoadingTabKey(null);
    });
  }, [activeTabKey, staleTabs, tabFetchers]);

  const fetchStagedVoyages = useCallback(async () => {
    try {
      const response = await getStagedVoyages();
      setStagedVoyages(response || []);
    } catch (error) {
      setStagedVoyages([]);
    }
  }, []);

  const fetchStagedCount = useCallback(async () => {
    try {
      const count = await getStagedVoyagesCount();
      setStagedCount(count || 0);
    } catch (error) {
      setStagedCount(0);
    }
  }, []);

  useEffect(() => {
    fetchStagedCount();
  }, [fetchStagedCount]);

  useEffect(() => {
    resetSelectedVoyageId();
    // Mark every tab's data stale rather than eagerly refetching all of it —
    // only the currently active tab refetches immediately (see activation effect above).
    invalidateAllTabs();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    filters.departureCountry,
    filters.departurePort,
    filters.destinationCountry,
    filters.destinationPort,
    filters.startDateETD,
    filters.endDateETD,
    filters.startDateETA,
    filters.endDateETA,
    filters.shippingCompany,
    filters.portOperator,
    filters.groupBy,
    filters.market,
    filters.imo,
    activeScenario?.id,
  ]);

  const value = useMemo<ReportingCoordinationContextValue>(
    () => ({
      isLoading,
      staleTabs,
      activeTabKey,
      loadingTabKey,
      tabFetchers,
      invalidateAllTabs,
      setActiveTab,
      fetchAllData,
      stagedVoyages,
      stagedCount,
      fetchStagedVoyages,
      fetchStagedCount,
    }),
    [
      isLoading,
      staleTabs,
      activeTabKey,
      loadingTabKey,
      tabFetchers,
      invalidateAllTabs,
      setActiveTab,
      fetchAllData,
      stagedVoyages,
      stagedCount,
      fetchStagedVoyages,
      fetchStagedCount,
    ]
  );

  return (
    <ReportingCoordinationContext.Provider value={value}>
      {children}
    </ReportingCoordinationContext.Provider>
  );
};

export const useReportingCoordination =
  (): ReportingCoordinationContextValue => {
    const ctx = useContext(ReportingCoordinationContext);
    if (ctx === null) {
      throw new Error(
        'useReportingCoordination must be used within a <DataProviderVesselReporting>'
      );
    }
    return ctx;
  };

// ── Thin composition wrapper ──────────────────────────────────────────
// Nests all domain-specific providers with the coordination layer
// innermost so it can consume every sub-context it orchestrates.
export const DataProviderVesselReporting = ({
  children,
}: PropsWithChildren) => (
  <ReportingFiltersProvider>
    <ReportingChartDataProvider>
      <ReportingVoyageDataProvider>
        <ReportingShipmentDataProvider>
          <ReportingStockDataProvider>
            <ReportingShipViewDataProvider>
              <ReportingCoordinationProvider>
                {children}
              </ReportingCoordinationProvider>
            </ReportingShipViewDataProvider>
          </ReportingStockDataProvider>
        </ReportingShipmentDataProvider>
      </ReportingVoyageDataProvider>
    </ReportingChartDataProvider>
  </ReportingFiltersProvider>
);

export type DataVesselReportingValue = Pick<
  ReportingFiltersContextValue,
  'filters' | 'setFilters' | 'markets'
> &
  Omit<ReportingChartDataContextValue, 'fetchChartsTabData'> &
  Omit<
    ReportingVoyageDataContextValue,
    'resetSelectedVoyageId' | 'fetchTbnVoyages'
  > &
  ReportingShipmentDataContextValue &
  ReportingStockDataContextValue &
  ReportingShipViewDataContextValue &
  Pick<
    ReportingCoordinationContextValue,
    | 'isLoading'
    | 'loadingTabKey'
    | 'setActiveTab'
    | 'fetchAllData'
    | 'stagedVoyages'
    | 'stagedCount'
    | 'fetchStagedVoyages'
    | 'fetchStagedCount'
  >;

// ── Backward-compatible facade ────────────────────────────────────────
// Merges values from the coordination layer and all sub-contexts so
// existing consumers require zero changes.
export const useDataVesselReporting = (): DataVesselReportingValue => {
  const { filters, setFilters, markets } = useReportingFilters();
  const {
    mapWidgetData,
    barChartWidgetDataETD,
    barChartWidgetDataETA,
    barChartPortWidgetDataETA,
    barChartPortWidgetDataETD,
    summaryData,
    tableQuarterlyData,
    tableMonthlyData,
    tableStatisticsData,
  } = useReportingChartData();
  const {
    voyages,
    voyagesBase,
    currentPage,
    totalPages,
    fetchVoyagesPage,
    selectedVoyageId,
    setSelectedVoyageId,
    clearSelectedVoyageId,
    tbnVoyages,
  } = useReportingVoyageData();
  const {
    shipmentDetails,
    shipmentDetailsBase,
    shipmentPageInfo,
    shipmentTotals,
    shipmentNearDateRowIndex,
    fetchShipmentDetailsPage,
    isShipmentDetailsLoading,
  } = useReportingShipmentData();
  const {
    stockData,
    baseStockData,
    isStockLoading,
    fetchStockData,
    fetchStockDetails,
  } = useReportingStockData();
  const {
    shipViewGroups,
    shipViewGroupsBase,
    shipViewCurrentPage,
    shipViewTotalPages,
    fetchShipViewPage,
  } = useReportingShipViewData();
  const {
    isLoading,
    loadingTabKey,
    setActiveTab,
    fetchAllData,
    stagedVoyages,
    stagedCount,
    fetchStagedVoyages,
    fetchStagedCount,
  } = useReportingCoordination();

  return {
    filters,
    setFilters,
    isLoading,
    mapWidgetData,
    barChartWidgetDataETD,
    barChartWidgetDataETA,
    barChartPortWidgetDataETA,
    barChartPortWidgetDataETD,
    summaryData,
    tableQuarterlyData,
    tableMonthlyData,
    stockData,
    baseStockData,
    isStockLoading,
    isShipmentDetailsLoading,
    fetchStockData,
    fetchStockDetails,
    markets,
    voyages,
    voyagesBase,
    currentPage,
    totalPages,
    fetchVoyagesPage,
    selectedVoyageId,
    setSelectedVoyageId,
    clearSelectedVoyageId,
    tbnVoyages,
    shipmentDetails,
    shipmentDetailsBase,
    shipmentPageInfo,
    shipmentTotals,
    shipmentNearDateRowIndex,
    fetchShipmentDetailsPage,
    tableStatisticsData,
    shipViewGroups,
    shipViewGroupsBase,
    shipViewCurrentPage,
    shipViewTotalPages,
    fetchShipViewPage,
    fetchAllData,
    setActiveTab,
    loadingTabKey,
    stagedVoyages,
    stagedCount,
    fetchStagedVoyages,
    fetchStagedCount,
  };
};

// ── Domain-specific hook aliases ──────────────────────────────────────
// Allow consumers to depend only on the slice of state they need,
// reducing unnecessary re-renders compared to the monolithic facade.
export { useReportingFilters } from './reporting/ReportingFiltersContext';
export { useReportingVoyageData as useVoyageData } from './reporting/ReportingVoyageDataContext';
export { useReportingShipmentData as useShipmentData } from './reporting/ReportingShipmentDataContext';
export { useReportingStockData as useStockData } from './reporting/ReportingStockDataContext';
export { useReportingChartData as useChartData } from './reporting/ReportingChartDataContext';
export { useReportingShipViewData as useShipViewData } from './reporting/ReportingShipViewDataContext';
